// src/pages/Main/frontContent.js

// The front page's copy: lists that more than one page builds from. Ported from docs/mockups/front_page/src.

export const APPS = {
  missions: {
    title: "Missions",
    status: ["live", ""],
    domain: "Engineering",
    widget: "app.missions.control",
    path: "/apps",
    // The first picture is the one the main page's card shows; /apps shows them all as a carousel.
    shots: [
      {
        name: "app_missions",
        alt: "Mission Control: part of a mission's dependency graph, each cell showing its status: two cells waiting "
          + "for a person, the six planned cells behind them, up to the rollout and the work that follows it, and one "
          + "cell already done.",
        caption: "Two cells wait for a person, and the rollout waits on both.",
      },
      {
        name: "app_missions_board",
        alt: "Mission Control's Board view: the same mission's 22 cells in lanes by stage, seven in draft, seven "
          + "waiting, two that need a person and six done.",
        caption: "The same mission by stage, from draft to done.",
      },
      {
        name: "app_missions_overview",
        alt: "Mission Control's Graph view fitted to the whole mission: all 22 cells, each with its status, wired "
          + "to the cells it depends on.",
        caption: "The whole mission at once, every cell with its status.",
      },
      {
        name: "app_missions_cell",
        alt: "One cell opened: its two coding-agent runs, a plan and an implementation, each with the file it "
          + "produced, beside the end of its meta thread, where the agent reports, a person answers the review and the "
          + "orchestrator marks the cell finished.",
        caption: "One cell opened: what the orchestrator did, what a person answered, and every run of the coding "
          + "agent.",
      },
    ],
    text: "Delegate work to coding agents. A mission is broken into cells wired into a graph, each worked by an "
      + "agent under a procedure and a policy, and a person approves what matters.",
    long: "Delegate work to coding agents. A mission is broken into cells wired into a graph, each cell worked by "
      + "an agent under the procedure and the policy it names, and a person approves what matters. Mission Control "
      + "shows what can move now, what is blocked and what is waiting on you.",
    govern: "Each cell runs under a policy and a permission posture; a push or a risky command stops for a person.",
    audit: "Who or what moved this cell, and what did the agent do in every turn?",
  },
  daytrader: {
    title: "Day Trader",
    status: ["in build", "muted"],
    domain: "Finance",
    widget: "app.daytrader.desk",
    path: "/apps",
    shots: [
      {
        name: "app_daytrader",
        alt: "The Day Trader desk during a replay: sleeves, a TSLA chart with entries and stops, and the order book.",
        caption: "A past day replayed on the desk.",
      },
    ],
    text: "Automatic and semi-automatic intraday trading for US equities. The strategy that is tested is the "
      + "strategy that trades, and the desk shows what every strategy did and why.",
    long: "Automatic and semi-automatic intraday trading for US equities, built only from BlobHub workflows, "
      + "session storage, a scheduler and one desk widget. One strategy file runs in the cloud and in the local lab "
      + "and makes the same decisions on the same data, and any past day replays in the cloud as if it were live.",
    govern: "Allocations and loss limits decide what goes through; one switch stops everything.",
    audit: "For this order: what did it see, which version of the strategy ran, and who changed it?",
  },
};

const gallery = (key, title, text, path) => ({ key, title, text, path: `/${path}`, shot: `gal_${key}` });

export const GALLERY = [
  gallery("deep_research", "Deep Research",
    "Plans a line of enquiry, searches the web, reads what it finds and writes a cited report.",
    "workflow-gallery/deep-research"),
  gallery("discussion_board", "Discussion Board",
    "A moderator on DeepSeek R1 drives a discussion between participants on GPT-4o, Claude and Gemini.",
    "workflow-gallery/discussion-board"),
  gallery("trip_planner", "Trip Planner",
    "Several models plan a trip together, calling flight, hotel and search tools against your preferences.",
    "workflow-gallery/trip-planner"),
  gallery("open_canvas", "Open Canvas",
    "A document an agent and a person write together, turn by turn, in one session.",
    "workflow-gallery/open-canvas"),
  gallery("basic_rag", "Basic RAG",
    "Chunk, embed, store and retrieve: the smallest retrieval loop, wired to a vector store.",
    "workflow-gallery/basic-rag"),
  gallery("finance_ticker_data", "Finance Ticker Data",
    "Pulls market data for a ticker and writes an analysis of it with a model.",
    "industries/finance-ticker-data"),
];

const lab = (key, title, text, path, type, tags = []) => ({
  key, title, text, path: `/${path}`, type, tags, shot: `labs_${key}`,
});
const ONNX = "blobhub.model.onnx";
const GRAPH = "blobhub.graph.orientdb";
const DATASET = "blobhub.dataset.nlp";

export const LABS = {
  onnx: [
    lab("object_detection", "Object Detection", "Identifies individual objects in an image.",
      "onnx-vision-models/object-detection", ONNX, ["vision", "object-detection"]),
    lab("super_resolution", "Super Resolution", "Increases the resolution of an image by an upscale factor.",
      "onnx-vision-models/super-resolution", ONNX, ["vision", "upscale"]),
    lab("bidaf", "BiDAF", "Bi-directional Attention Flow network for question answering.",
      "onnx-text-models/bi-att-flow", ONNX, ["nlp", "squad", "question-answering"]),
  ],
  graphs: [
    lab("movies", "Movies", "The Movies Dataset as a property graph.", "world/movies", GRAPH,
      ["graphs", "movies"]),
    lab("wikimedia", "Wikimedia", "Kensho Derived Wikimedia Dataset.", "world/wikimedia", GRAPH,
      ["graphs", "wikidata"]),
    lab("conceptnet", "ConceptNet", "An open, multilingual knowledge graph.", "nlp/conceptnet", GRAPH,
      ["graphs", "nlp"]),
  ],
  datasets: [
    lab("ax_b", "AX-b", "Broadcoverage Diagnostics.", "superglue/ax-b", DATASET),
    lab("ax_g", "AX-g", "Winogender Schema Diagnostics.", "superglue/ax-g", DATASET),
    lab("boolq", "BoolQ", "Boolean Questions.", "superglue/boolq", DATASET),
    lab("cb", "CB", "Commitment Bank.", "superglue/cb", DATASET),
    lab("copa", "COPA", "Choice of Plausible Alternatives.", "superglue/copa", DATASET),
    lab("multirc", "MultiRC", "Multi-Sentence Reading Comprehension.", "superglue/multirc", DATASET),
    lab("record", "ReCoRD", "Reading Comprehension with Commonsense Reasoning.", "superglue/record", DATASET),
    lab("rte", "RTE", "Recognizing Textual Entailment.", "superglue/rte", DATASET),
    lab("wic", "WiC", "Words in Context.", "superglue/wic", DATASET),
    lab("wsc", "WSC", "The Winograd Schema Challenge.", "superglue/wsc", DATASET),
  ],
};

export const EXAMPLES = [
  {
    domain: "Research",
    title: "Deep Research",
    text: "Takes a question, plans the search, reads the sources and writes a cited report.",
    govern: "A person signs off before a report is published.",
  },
  {
    domain: "Customer support",
    title: "Support Desk",
    text: "Triages tickets, drafts replies and resolves the routine ones.",
    govern: "Refunds above a limit wait for a person.",
  },
  {
    domain: "Legal and compliance",
    title: "Contract Review",
    text: "Checks contracts against the company playbook and proposes redlines.",
    govern: "Agents propose, counsel approves.",
  },
  {
    domain: "Security and IT",
    title: "Alert Triage",
    text: "Investigates alerts, closes false positives and escalates the real ones.",
    govern: "Containment needs a second person.",
  },
  {
    domain: "Sales and marketing",
    title: "Account Research",
    text: "Builds a brief on each target account and drafts the outreach.",
    govern: "Nothing is sent unapproved.",
  },
  {
    domain: "Supply chain",
    title: "Replenishment Planner",
    text: "Forecasts demand and proposes purchase orders.",
    govern: "Orders off the supplier list need a buyer.",
  },
];

export const QUESTIONS = [
  {
    key: "build",
    label: "Build",
    when: "before it runs",
    text: "Can the next team ship an agent that works with the last team's?",
  },
  {
    key: "govern",
    label: "Govern",
    when: "while it runs",
    text: "What are our agents allowed to do, and who decided that?",
  },
  {
    key: "audit",
    label: "Audit",
    when: "after it ran",
    text: "What did that agent actually do, when, and on whose say?",
  },
];

export const PRIMITIVES = [
  "sessions", "threads", "shared objects", "workflows", "schedules", "credentials", "permissions", "policies",
];

export const SURFACES = [
  {
    key: "api",
    title: "API",
    code: "api.blobhub.io/v1",
    text: "REST for everything, with a key scoped to an organization or a single blob.",
  },
  {
    key: "web",
    title: "Web",
    code: "blobhub.io",
    text: "The workflow editor, the debugger, playgrounds and apps.",
  },
  {
    key: "sdk",
    title: "SDK",
    code: "pip install blobhub",
    text: "Run workflows and read their record from Python and notebooks.",
  },
  {
    key: "cli",
    title: "CLI",
    code: "pip install blobhub-cli",
    text: "Build, version and deploy workflows and schedules from a terminal or CI.",
  },
  {
    key: "worker",
    title: "Worker",
    code: "pip install blobhub-worker",
    text: "Run Claude Code, Codex and Antigravity on your own machines, inside your guardrails.",
  },
];

export const COMPOSE_PARTS = ["sessions", "threads", "workflows", "schedules", "policies", "credentials", "widgets"];

export const COMPOSE_DOMAINS = ["Research", "Support", "Legal", "Security", "Sales", "Supply chain"];
